import i18nFactory from '@yandex-int/i18n';
import { combineEpics, Epic, ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';
import { getType } from 'typesafe-actions';

import { ApiMethodExecutingError } from '../../service/Errors';
import { EpicDeps } from '../../service/StoreFactory';
import { State } from '../../typings/state';
import {
    applyGraphicCreativeFile,
    changeAddButtonAvailable,
    clearOptionErrors,
    hidePreviewLoading,
    setEditingFileInfo,
    shouldCreateVideoCreative,
    showOptionErrors,
    showPreviewLoading
} from '../AddCreativePage/actions';
import { getAdaptiveCreativeState, getSmartCentersValue } from '../AddCreativePage/AddAdaptiveCreative/helpers';
import {
    getCommonCreativeState,
    getCreativeSizeData,
    getCropSizesValue
} from '../AddCreativePage/AddCommonCreative/helpers';
import { getVideoCreativeState } from '../AddCreativePage/AddVideoCreative/helpers';
import { getIsCpmAudio } from '../AddCreativePage/AddVideoCreative/helpers';
import { selectCreativeOptionGroupValue } from '../AddCreativePage/selectors';
import { CanvasAction } from '../common/actions';
import {
    getErrorItems,
    getValidationErrorData,
    isGraphicCreatives,
    isOverlay,
    isVideoAddition
} from '../common/helpers';
import { ApiItemStatus, FetchCommonFileDataResponse, FetchVideoDataResponse } from '../common/requestsTypings';
import { ResourceType } from '../common/typings';
import { openModal } from '../CreativeImageCropModal/actions';
import { openModal as openSmartCenter } from '../CreativeSmartCenterModal/actions';
import { reloadRecentCollection } from '../GalleryPane/actions';
import { disableAudioOption, enableAudioOption } from '../GalleryPane/epics';
import { closeModal, showLinkErrors, uploadByLink, UploadByLinkAction } from '../UploadByLinkModal/actions';
import { GLOBAL_ERRORS_PATH, URL_FORM_ERRORS_PATH } from '../UploadByLinkModal/typings';

import {
    crop,
    CropAction,
    hideLoading,
    init,
    InitAction,
    setFile,
    setPreview,
    showErrors,
    smartCenter,
    SmartCenterAction,
    uploadCancel,
    UploadCancelAction,
    uploadCancelSuccess,
    uploadSourceSelected,
    UploadSourceSelectedAction,
    waitForFileLoaded,
    WaitForFileLoadedAction
} from './actions';
// tslint:disable-next-line no-external-i18n-import
import * as keyset from './FileEditor.i18n';
import { fileEditorsLongUpload, fileEditorsUploading, isVideoConverting, shouldUpdatePreview } from './helpers';
import { selectFileEditorState } from './selectors';
import { FileEditorOptions } from './typings';

const i18n = i18nFactory(keyset);

const showEditorErrors = (editorId: string, message: string, options?: FileEditorOptions) => {
    const error = getValidationErrorData(message);
    const showErrorsActions: CanvasAction[] = [showErrors(editorId, error)];

    if (!options) {
        return showErrorsActions;
    }

    showErrorsActions.push(showOptionErrors(options.groupName, options.name, [error]));

    return showErrorsActions;
};

const showUploadingErrors = (
    editorId: string,
    message: string,
    options?: FileEditorOptions,
    needToHideLoading?: boolean,
    needToRestoreFileId?: boolean
) => {
    const showErrorsActions: CanvasAction[] = showEditorErrors(editorId, message, options);

    if (needToHideLoading) {
        showErrorsActions.push(hidePreviewLoading());
    }

    if (!options) {
        return showErrorsActions;
    }

    if (needToRestoreFileId) {
        showErrorsActions.push(...enableAudioOption(options));
    }

    return showErrorsActions;
};

const showFileUploadingErrors = (
    editorId: string,
    message: string,
    options?: FileEditorOptions,
    needToHideLoading?: boolean,
    needToRestoreFileId?: boolean
) => {
    return showUploadingErrors(editorId, message, options, needToHideLoading, needToRestoreFileId).concat(
        changeAddButtonAvailable(true)
    );
};

const showLinkModalErrors = (path: string, message: string) => {
    return of(showLinkErrors(getErrorItems(path, message)));
};

export const fetchFileDataEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(init)),
        mergeMap(
            (action: InitAction): Observable<CanvasAction> => {
                const {
                    canvasEditor: { sharedData }
                } = store$.value;
                const { editorId, options, fileId } = action;

                if (!fileId) {
                    return EMPTY;
                }

                if (isGraphicCreatives(sharedData.creativeType)) {
                    return from(deps.canvasApi.addCommonCreative.fetchFileData(fileId, sharedData)).pipe(
                        map((response: FetchCommonFileDataResponse) => setPreview(editorId, response, false)),
                        catchError(error =>
                            showEditorErrors(editorId, error.message || error.getNativeError().message, options)
                        )
                    );
                }

                return from(deps.canvasApi.addVideoCreative.fetchVideoAdditionFileData(fileId, sharedData)).pipe(
                    switchMap((response: FetchVideoDataResponse) => {
                        const fetchSuccess: CanvasAction[] = [setPreview(editorId, response, true)];

                        if (
                            options &&
                            !getIsCpmAudio(sharedData) &&
                            response.type === ResourceType.video &&
                            response.stock_file_id === undefined
                        ) {
                            fetchSuccess.push(...disableAudioOption(options));
                        }

                        return fetchSuccess;
                    }),
                    catchError(error =>
                        showEditorErrors(editorId, error.message || error.getNativeError().message, options)
                    )
                );
            }
        )
    );
};

export const uploadFileEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(uploadSourceSelected)),
        filter(() => {
            const {
                canvasEditor: { sharedData }
            } = store$.value;

            return isVideoAddition(sharedData.creativeType) || isGraphicCreatives(sharedData.creativeType);
        }),
        mergeMap((action: UploadSourceSelectedAction): Observable<CanvasAction> | CanvasAction[] => {
            const {
                canvasEditor: {
                    sharedData,
                    canvas: { fileEditors }
                }
            } = store$.value;
            const { editorId, files, resourceType } = action;

            const { options, fromGallery } = selectFileEditorState(fileEditors, editorId);
            const needToRestoreAudio = !getIsCpmAudio(sharedData) && fromGallery && resourceType === ResourceType.video;

            const needEnableAddButton = !fileEditorsUploading(fileEditors, editorId, true);
            const needHideLoading = needEnableAddButton && !fileEditorsLongUpload(fileEditors, editorId);

            if (!options) {
                return showFileUploadingErrors(editorId, i18n('upload-error'));
            }

            if (isGraphicCreatives(sharedData.creativeType)) {
                return from(deps.canvasApi.addCommonCreative.uploadFile(files[0], sharedData)).pipe(
                    switchMap((response: FetchCommonFileDataResponse): Observable<CanvasAction> | CanvasAction[] => {
                        if (response.properties && response.message) {
                            return showFileUploadingErrors(editorId, response.message, options);
                        }

                        return [
                            applyGraphicCreativeFile(editorId, response, options.groupName),
                            reloadRecentCollection(options.resourceType),
                            changeAddButtonAvailable(true),
                            hideLoading(editorId)
                        ];
                    }),
                    catchError((error: ApiMethodExecutingError) =>
                        showLinkModalErrors(GLOBAL_ERRORS_PATH, error.getNativeError().message)
                    )
                );
            }

            return from(
                deps.canvasApi.addVideoCreative.uploadVideoAdditionFile(resourceType, files[0], sharedData)
            ).pipe(
                switchMap((response: FetchVideoDataResponse): Observable<CanvasAction> | CanvasAction[] => {
                    if (response.properties && response.message) {
                        return showFileUploadingErrors(editorId, response.message, options, true, needToRestoreAudio);
                    }

                    const fileConverting: CanvasAction[] = [waitForFileLoaded(editorId, response.id)];

                    const fileLoaded: CanvasAction[] = [
                        setFile(editorId, response),
                        reloadRecentCollection(resourceType)
                    ];

                    if (needEnableAddButton) {
                        fileLoaded.push(changeAddButtonAvailable(true));
                        fileConverting.push(changeAddButtonAvailable(true));
                    }

                    if (needHideLoading) {
                        fileLoaded.push(hidePreviewLoading());
                    }

                    return isVideoConverting(response) ? fileConverting : fileLoaded;
                }),
                catchError((error: ApiMethodExecutingError) =>
                    showFileUploadingErrors(editorId, error.getNativeError().message, options, true, needToRestoreAudio)
                )
            );
        })
    );
};

export const uploadOverlayFileEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(uploadSourceSelected)),
        filter(() => {
            const {
                canvasEditor: { sharedData }
            } = store$.value;

            return isOverlay(sharedData.creativeType);
        }),
        mergeMap((action: UploadSourceSelectedAction): Observable<CanvasAction> | CanvasAction[] => {
            const {
                canvasEditor: {
                    sharedData,
                    canvas: { fileEditors }
                }
            } = store$.value;
            const { editorId, files, resourceType } = action;

            const { options } = selectFileEditorState(fileEditors, editorId);

            if (!options) {
                return showFileUploadingErrors(editorId, i18n('upload-error'));
            }

            return from(deps.canvasApi.addOverlayCreative.uploadOverlayFile(resourceType, files[0], sharedData)).pipe(
                switchMap((response: FetchVideoDataResponse): Observable<CanvasAction> | CanvasAction[] => {
                    if (response.properties && response.message) {
                        return showFileUploadingErrors(editorId, response.message, options);
                    }

                    return [setFile(editorId, response), changeAddButtonAvailable(true)];
                }),
                catchError((error: ApiMethodExecutingError) =>
                    showFileUploadingErrors(editorId, error.getNativeError().message, options)
                )
            );
        })
    );
};

const uploadFileByLinkEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(uploadByLink)),
        switchMap((action: UploadByLinkAction): Observable<CanvasAction> | CanvasAction[] => {
            const {
                canvasEditor: {
                    sharedData,
                    canvas: { fileEditors }
                }
            } = store$.value;
            const { options } = selectFileEditorState(fileEditors, action.editorId);

            if (!options) {
                return showLinkModalErrors(GLOBAL_ERRORS_PATH, i18n('upload-error'));
            }

            if (isGraphicCreatives(sharedData.creativeType)) {
                return from(deps.canvasApi.addCommonCreative.uploadFileByLink(action.value, sharedData)).pipe(
                    switchMap((response: FetchCommonFileDataResponse): Observable<CanvasAction> | CanvasAction[] => {
                        if (response.properties && response.message) {
                            return showLinkModalErrors(URL_FORM_ERRORS_PATH, response.message);
                        }

                        return [
                            clearOptionErrors(options.groupName, options.name),
                            closeModal(),
                            applyGraphicCreativeFile(action.editorId, response, options.groupName),
                            reloadRecentCollection(options.resourceType)
                        ];
                    }),
                    catchError((error: ApiMethodExecutingError) =>
                        showLinkModalErrors(GLOBAL_ERRORS_PATH, error.getNativeError().message)
                    )
                );
            }

            const needToChangeAudio = !getIsCpmAudio(sharedData) && options.resourceType === ResourceType.video;

            return from(
                deps.canvasApi.addVideoCreative.uploadVideoAdditionFileByLink(action.urlType, action.value, sharedData)
            ).pipe(
                switchMap((response: FetchVideoDataResponse): Observable<CanvasAction> | CanvasAction[] => {
                    if (response.properties && response.message) {
                        return showLinkModalErrors(URL_FORM_ERRORS_PATH, response.message);
                    }

                    const success: CanvasAction[] = [clearOptionErrors(options.groupName, options.name), closeModal()];

                    // У пользовательских видеофайлов нужно отключать аудио CANVAS-472
                    if (needToChangeAudio) {
                        success.push(...disableAudioOption(options));
                    }

                    const fileConverting: CanvasAction[] = [
                        waitForFileLoaded(action.editorId, response.id),
                        showPreviewLoading(),
                        ...success
                    ];

                    const fileLoaded: CanvasAction[] = [
                        setFile(action.editorId, response),
                        reloadRecentCollection(options.resourceType),
                        ...success
                    ];

                    return isVideoConverting(response) ? fileConverting : fileLoaded;
                }),
                catchError((error: ApiMethodExecutingError) =>
                    showLinkModalErrors(GLOBAL_ERRORS_PATH, error.getNativeError().message)
                )
            );
        })
    );
};

export const waitForFileLoadedEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(waitForFileLoaded)),
        mergeMap((action: WaitForFileLoadedAction) => {
            const {
                sharedData,
                canvas: { fileEditors, addCreative }
            } = store$.value.canvasEditor;
            const { fileId, editorId } = action;
            const addVideoCreative = getVideoCreativeState(addCreative);

            if (!addVideoCreative || !fileId || !fileEditors[editorId].upload) {
                return EMPTY;
            }

            const { options, fromGallery } = selectFileEditorState(fileEditors, editorId);
            const needHideLoading = !fileEditorsUploading(fileEditors, editorId);

            if (!options) {
                return showUploadingErrors(editorId, i18n('upload-error'));
            }

            const needToRestoreAudio =
                !getIsCpmAudio(sharedData) && fromGallery && options.resourceType === ResourceType.video;

            return from(deps.canvasApi.addVideoCreative.fetchVideoAdditionFileData(fileId, sharedData)).pipe(
                switchMap((response: FetchVideoDataResponse): Observable<CanvasAction> | CanvasAction[] => {
                    if (response.type !== ResourceType.video || response.status === ApiItemStatus.error) {
                        return showUploadingErrors(
                            editorId,
                            response.error_message || i18n('upload-error'),
                            options,
                            needHideLoading,
                            needToRestoreAudio
                        );
                    }

                    // если файл обработан
                    if (response.status === ApiItemStatus.ready) {
                        const fileReady: CanvasAction[] = [
                            setFile(editorId, response),
                            reloadRecentCollection(options.resourceType)
                        ];

                        if (needHideLoading) {
                            fileReady.push(hidePreviewLoading());
                        }

                        // если ранее не была нажата кнопка создать креатив или какой-то загрузчик еще не догрузился,
                        // то просто выставляем файл, иначе - еще и создаем
                        return !addVideoCreative.createButtonPressed || fileEditorsLongUpload(fileEditors, editorId)
                            ? fileReady
                            : fileReady.concat(shouldCreateVideoCreative());
                    }

                    /**
                     * В процессе поллинга смотрим на приехавший thumbnailUrl
                     * если он есть, проверяем не устанавливали ли его раньше
                     * если он отличается или не был ещё проинициализирован
                     * то установим на тот, что приехал
                     */
                    if (response.thumbnailUrl && shouldUpdatePreview(fileEditors, editorId, response.thumbnailUrl)) {
                        return [waitForFileLoaded(editorId, fileId), setPreview(editorId, response)];
                    }

                    return of(waitForFileLoaded(editorId, fileId));
                }),
                catchError((error: ApiMethodExecutingError) =>
                    showUploadingErrors(
                        action.editorId,
                        error.getNativeError().message,
                        options,
                        needHideLoading,
                        needToRestoreAudio
                    )
                ),
                delay(1000),
                takeUntil(action$.ofType(uploadCancel))
            );
        })
    );
};

const uploadCancelEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(uploadCancel)),
        mergeMap((action: UploadCancelAction) => {
            const {
                canvasEditor: {
                    sharedData,
                    canvas: { fileEditors }
                }
            } = store$.value;
            const { editorId } = action;
            const { options, loadingFileId, fromGallery } = selectFileEditorState(fileEditors, editorId);

            if (!loadingFileId || !options) {
                return EMPTY;
            }

            const needToRestoreAudio =
                !getIsCpmAudio(sharedData) && fromGallery && options.resourceType === ResourceType.video;

            return from(deps.canvasApi.addVideoCreative.deleteVideoAdditionFileData(loadingFileId, sharedData)).pipe(
                switchMap((): CanvasAction[] => {
                    const cancelSuccess: CanvasAction[] = [uploadCancelSuccess(editorId), hidePreviewLoading()];

                    return needToRestoreAudio ? cancelSuccess.concat(enableAudioOption(options)) : cancelSuccess;
                }),
                catchError((error: ApiMethodExecutingError) =>
                    showEditorErrors(editorId, error.getNativeError().message, options)
                )
            );
        })
    );
};

const fileLoadingStartEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(uploadSourceSelected)),
        mergeMap((action: UploadSourceSelectedAction) => {
            const {
                canvasEditor: {
                    sharedData,
                    canvas: { fileEditors }
                }
            } = store$.value;
            const { options } = selectFileEditorState(fileEditors, action.editorId);

            if (!options) {
                return showFileUploadingErrors(action.editorId, i18n('upload-error'));
            }

            const result: CanvasAction[] = [
                changeAddButtonAvailable(false),
                clearOptionErrors(options.groupName, options.name)
            ];

            if (isGraphicCreatives(sharedData.creativeType)) {
                return result;
            }

            result.push(showPreviewLoading());

            // У пользовательских видеофайлов нужно отключать аудио CANVAS-472
            if (!getIsCpmAudio(sharedData) && options.resourceType === ResourceType.video) {
                result.push(...disableAudioOption(options));
            }

            return result;
        })
    );
};

const cropEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(crop)),
        switchMap((action: CropAction) => {
            const {
                fileEditors,
                commonData: { ideaId }
            } = store$.value.canvasEditor.canvas;
            const { cropSourceUrl, url, fileId, options } = selectFileEditorState(fileEditors, action.editorId);
            const addCreative = getCommonCreativeState(store$.value.canvasEditor.canvas.addCreative);

            if (!addCreative || !cropSourceUrl || !url || !fileId || !options) {
                return EMPTY;
            }

            const { currentId, creativeOptions, items, ideaFilesCropped } = addCreative;

            if (currentId === undefined || !fileId || !creativeOptions || !items) {
                return EMPTY;
            }

            const mediaSetId = selectCreativeOptionGroupValue(creativeOptions, options.groupName).mediaSet;
            const isEditMode = !ideaId || ideaFilesCropped;

            if (!mediaSetId) {
                return EMPTY;
            }

            return [
                openModal(
                    {
                        src: cropSourceUrl,
                        resourceId: fileId
                    },
                    getCreativeSizeData(currentId, items),
                    isEditMode,
                    isEditMode ? getCropSizesValue(items) : undefined
                ),
                setEditingFileInfo({
                    editorId: action.editorId,
                    fileId,
                    cropSourceUrl,
                    mediaSetId,
                    origSrc: url
                })
            ];
        })
    );
};

const smartCenterEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(smartCenter)),
        switchMap((action: SmartCenterAction) => {
            const {
                fileEditors,
                commonData: { ideaId }
            } = store$.value.canvasEditor.canvas;
            const { cropSourceUrl, url, fileId, options } = selectFileEditorState(fileEditors, action.editorId);
            const addCreative = getAdaptiveCreativeState(store$.value.canvasEditor.canvas.addCreative);

            if (!addCreative || !cropSourceUrl || !fileId || !options || !url) {
                return EMPTY;
            }

            const { creativeOptions, item, ideaFilesCropped } = addCreative;

            if (!fileId || !creativeOptions || !item) {
                return EMPTY;
            }

            const mediaSetId = selectCreativeOptionGroupValue(creativeOptions, options.groupName).mediaSet;
            const isEditMode = !ideaId || ideaFilesCropped;

            if (!mediaSetId) {
                return EMPTY;
            }

            return [
                openSmartCenter(
                    {
                        src: cropSourceUrl,
                        resourceId: fileId
                    },
                    url,
                    isEditMode ? getSmartCentersValue(item, mediaSetId) : undefined
                ),
                setEditingFileInfo({
                    editorId: action.editorId,
                    fileId,
                    cropSourceUrl,
                    mediaSetId,
                    origSrc: url
                })
            ];
        })
    );
};

export const fileEditorEpics = combineEpics(
    fetchFileDataEpic,
    uploadFileByLinkEpic,
    uploadFileEpic,
    uploadOverlayFileEpic,
    waitForFileLoadedEpic,
    uploadCancelEpic,
    fileLoadingStartEpic,
    cropEpic,
    smartCenterEpic
);
